
import { useState } from 'react';

interface GalleryImage {
  url: string;
  caption: string;
}

interface EventGalleryProps {
  gallery: GalleryImage[];
  title: string;
  before: string;
  after: string;
}

export default function EventGallery({ gallery, title, before, after }: EventGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const allImages = [
    { url: before, caption: 'Before Transformation' },
    { url: after, caption: 'After Transformation' },
    ...gallery,
  ];

  const openLightbox = (index: number) => {
    setActiveIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const goNext = () => setActiveIndex((prev) => (prev + 1) % allImages.length);
  const goPrev = () => setActiveIndex((prev) => (prev - 1 + allImages.length) % allImages.length);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-px bg-emerald-400"></div>
          <span className="text-emerald-600 text-xs font-semibold tracking-[0.2em] uppercase">Photo Gallery</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-display text-stone-900 mb-12">
          Event <span className="italic text-stone-400">Highlights</span>
        </h2>

        {/* Before / After Row */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div
            className="relative group cursor-pointer rounded-2xl overflow-hidden"
            onClick={() => openLightbox(0)}
          >
            <div className="w-full h-64 overflow-hidden">
              <img src={before} alt="Before" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="absolute inset-0 bg-stone-900/30 group-hover:bg-stone-900/10 transition-colors"></div>
            <span className="absolute top-4 left-4 px-3 py-1.5 bg-stone-900/70 backdrop-blur-sm text-white text-xs font-semibold rounded-full">Before</span>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <i className="ri-zoom-in-line text-white text-xl"></i>
              </div>
            </div>
          </div>
          <div
            className="relative group cursor-pointer rounded-2xl overflow-hidden"
            onClick={() => openLightbox(1)}
          >
            <div className="w-full h-64 overflow-hidden">
              <img src={after} alt="After" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="absolute inset-0 bg-stone-900/30 group-hover:bg-stone-900/10 transition-colors"></div>
            <span className="absolute top-4 left-4 px-3 py-1.5 bg-emerald-600/80 backdrop-blur-sm text-white text-xs font-semibold rounded-full">After</span>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <i className="ri-zoom-in-line text-white text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {gallery.map((img, idx) => (
            <div
              key={idx}
              className="relative group cursor-pointer rounded-2xl overflow-hidden"
              onClick={() => openLightbox(idx + 2)}
            >
              <div className="w-full h-56 overflow-hidden">
                <img src={img.url} alt={img.caption} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="absolute inset-0 bg-stone-900/20 group-hover:bg-stone-900/0 transition-colors"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-stone-900/70 to-transparent">
                <p className="text-white text-sm font-medium">{img.caption}</p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-stone-950/95 flex items-center justify-center" onClick={closeLightbox}>
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors cursor-pointer z-10"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors cursor-pointer z-10"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          <div className="max-w-5xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-full h-[70vh] rounded-2xl overflow-hidden">
              <img
                src={allImages[activeIndex].url}
                alt={allImages[activeIndex].caption}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="text-center mt-4">
              <p className="text-white text-lg font-medium">{allImages[activeIndex].caption}</p>
              <p className="text-white/50 text-sm mt-1">{activeIndex + 1} / {allImages.length}</p>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] pb-2">
            {allImages.map((img, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setActiveIndex(idx); }}
                className={`w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer border-2 transition-all ${
                  idx === activeIndex ? 'border-emerald-400 opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                }`}
              >
                <img src={img.url} alt={img.caption} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
